import { agentPageLabel, AGENT_COMMANDS, findAgentCommand, needsConfirmation, type AgentCommandSpec } from './catalog';
import { describeCurrentPage, getAgentHandler, waitForAgentHandler, type AgentArgs } from './registry';
import type { PlannedStep } from './templates';

export type AgentStepStatus = 'pending' | 'awaiting' | 'running' | 'done' | 'failed' | 'skipped';

export type AgentStep = PlannedStep & {
  key: string;
  status: AgentStepStatus;
  result?: string;
};

export type AgentPlan = {
  id: string;
  source: 'template' | 'llm';
  steps: AgentStep[];
  stopped: boolean;
};

let seq = 0;
const nextKey = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${(seq += 1)}`;

/**
 * 校验并规整一组步骤: 只保留目录里有的命令、目录里声明过的参数, 类型不对的直接拒绝。
 * 大模型给的计划和模板给的计划都走这里, 前端从不执行目录以外的东西。
 */
export function sanitizeSteps(raw: unknown): { steps: PlannedStep[]; rejected: string[] } {
  const steps: PlannedStep[] = [];
  const rejected: string[] = [];
  if (!Array.isArray(raw)) return { steps, rejected };
  for (const item of raw.slice(0, 8)) {
    const command = typeof item?.command === 'string' ? item.command : '';
    const spec = findAgentCommand(command);
    if (!spec) { rejected.push(command || '?'); continue; }
    const args = coerceArgs(spec, item?.args);
    if (args === null) { rejected.push(command); continue; }
    steps.push({ command, args, reason: typeof item?.reason === 'string' ? item.reason.slice(0, 200) : undefined });
  }
  return { steps, rejected };
}

function coerceArgs(spec: AgentCommandSpec, raw: unknown): AgentArgs | null {
  const input = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const out: AgentArgs = {};
  for (const param of spec.params ?? []) {
    const value = input[param.name];
    if (value === undefined || value === null || value === '') {
      if (param.required) return null;
      continue;
    }
    if (param.type === 'number') {
      const n = Number(value);
      if (!Number.isFinite(n)) return null;
      out[param.name] = n;
    } else if (param.type === 'number[]') {
      if (!Array.isArray(value)) return null;
      const list = value.map(Number).filter((n) => Number.isInteger(n) && n > 0);
      if (list.length !== value.length) return null;
      out[param.name] = list;
    } else if (param.type === 'enum') {
      if (!param.options?.some((o) => o.value === value)) return null;
      out[param.name] = value;
    } else {
      out[param.name] = String(value).slice(0, 200);
    }
  }
  return out;
}

export function createPlan(source: AgentPlan['source'], steps: PlannedStep[]): AgentPlan {
  return {
    id: nextKey('plan'),
    source,
    stopped: false,
    steps: steps.map((s) => ({ ...s, key: nextKey('step'), status: 'pending' })),
  };
}

export const stepNeedsConfirmation = (step: PlannedStep) => {
  const spec = findAgentCommand(step.command);
  return spec ? needsConfirmation(spec.kind) : true;
};

/** 给大模型看的命令目录 (精简, 不含实现)。 */
export const catalogForModel = () => AGENT_COMMANDS.map((c) => ({
  id: c.id,
  title: c.title,
  description: c.description,
  kind: c.kind,
  page: c.page ?? null,
  params: (c.params ?? []).map((p) => ({
    name: p.name, type: p.type, description: p.description, required: Boolean(p.required),
    ...(p.options ? { options: p.options.map((o) => `${o.value}=${o.label}`) } : {}),
  })),
}));

type ExecContext = {
  navigate: (path: string) => void;
  translate: (text: string, vars?: Record<string, string | number>) => string;
};

/** 执行一步, 返回给用户看的结果文字; 失败时抛错。 */
export async function executeStep(step: PlannedStep, ctx: ExecContext): Promise<string> {
  const t = ctx.translate;
  const spec = findAgentCommand(step.command);
  if (!spec) throw new Error(t('未知命令 {cmd}', { cmd: step.command }));

  if (spec.id === 'nav.goto') {
    const page = String(step.args.page);
    ctx.navigate(page);
    return t('已打开「{page}」', { page: t(agentPageLabel(page)) });
  }
  if (spec.id === 'page.status') {
    return describeCurrentPage() || t('当前页面没有可读取的状态');
  }

  if (!getAgentHandler(spec.id)) {
    if (spec.page && !window.location.pathname.startsWith(spec.page)) ctx.navigate(spec.page);
    const ready = await waitForAgentHandler(spec.id);
    if (!ready) throw new Error(t('「{page}」页面未就绪, 请确认已选择项目后重试', { page: t(agentPageLabel(spec.page ?? '')) }));
  }
  const handler = getAgentHandler(spec.id);
  if (!handler) throw new Error(t('页面已离开, 命令未执行'));
  const result = await handler(step.args);
  return typeof result === 'string' && result ? result : t('已执行');
}
